import React, {useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useTheme} from '@mui/material/styles';
import {IconBox, Ic} from './IconBox';
import {BaseCoverageRow} from './BaseCoverageRow';
import {fireGtmEvent} from '../utils/marketing.utils';

export function CoverageBasicPackage({removedCovers = {}, onToggleCover}) {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const theme = useTheme();

  const toggleOpen = () => {
    fireGtmEvent('Basic_coverage_details');
    setOpen((currentOpen) => !currentOpen);
  };

  const cardWrap = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 'var(--wobi-v2-radius-xl, 16px)',
    padding: open ? '6px clamp(12px,2vw,20px)' : '0',
    background: theme.palette.background.paper,
  };

  return (
    <div>
      <div style={cardWrap}>
        {/* Summary/toggle row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          padding: open ? '14px 4px' : '18px',
          flexWrap: 'wrap',
          borderBottom: open ? `1px solid ${theme.palette.divider}` : 'none',
        }}>
          <button
            type='button'
            onClick={toggleOpen}
            style={{
              order: 3,
              marginInlineStart: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.palette.secondary.dark,
              fontWeight: theme.typography.fontWeightBold,
              fontSize: theme.typography.pxToRem(14.5),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span><FormattedMessage id={open ? 'coverage.close' : 'coverage.details'}/></span>
            <span style={{
              display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease-out',
            }}>
              <Ic name='chevron' size={16} />
            </span>
          </button>

          {!open && (
            <div style={{
              order: 1, display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 auto', justifyContent: 'flex-start', textAlign: 'right',
            }}>
              <div style={{order: 2}}>
                <div style={{
                  fontWeight: theme.typography.fontWeightBlack,
                  fontSize: theme.typography.pxToRem(16),
                  color: theme.palette.text.primary,
                }}>
                  <FormattedMessage id='coverage.basicPackageTitle'/>
                </div>
                <div style={{fontSize: theme.typography.pxToRem(13), color: theme.palette.text.secondary, marginTop: 2}}>
                  <FormattedMessage id='coverage.basicPackageSummary'/>
                </div>
              </div>
              <span style={{order: 1}}><IconBox icon='shield' /></span>
            </div>
          )}

          {open && (
            <div style={{order: 1, textAlign: 'right', flex: '1 1 auto'}}>
              <div style={{fontWeight: theme.typography.fontWeightBlack, fontSize: theme.typography.pxToRem(16), color: theme.palette.text.primary}}>
                <FormattedMessage id='coverage.basicPackageTitle'/>
              </div>
              <div style={{fontSize: theme.typography.pxToRem(13), color: theme.palette.text.secondary, marginTop: 2}}>
                <FormattedMessage id='coverage.selectionSubtitle'/>
              </div>
            </div>
          )}
        </div>

        {/* Expanded rows */}
        {open && (
          <>
            <BaseCoverageRow
              icon='heart'
              title={intl.formatMessage({id: 'coverage.medicalTitle'})}
              description={intl.formatMessage({id: 'step1.tab4.medical_insurance_fulltext'})}
              mandatory
              last={false}
            />
            <BaseCoverageRow
              icon='plane'
              title={intl.formatMessage({id: 'step1.tab4.detection'})}
              description={intl.formatMessage({id: 'step1.tab4.detection_fulltext'})}
              removed={Boolean(removedCovers.isDetection)}
              onToggle={() => onToggleCover?.('isDetection')}
              last={false}
            />
            <BaseCoverageRow
              icon='users'
              title={intl.formatMessage({id: 'coverage.liabilityTitle'})}
              description={intl.formatMessage({id: 'step1.tab4.liability_fulltext'})}
              removed={Boolean(removedCovers.isLiable)}
              onToggle={() => onToggleCover?.('isLiable')}
              last
            />
          </>
        )}
      </div>

      {open && (
        <div style={{
          marginTop: 14,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: '10px 14px',
          background: theme.palette.info.light,
          borderRadius: 10,
          fontSize: theme.typography.pxToRem(13.5),
          color: theme.palette.info.dark,
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: 1.5,
        }}>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{flex: 'none', marginTop: 1}}
          >
            <circle cx='12' cy='12' r='10'/>
            <line x1='12' y1='8' x2='12' y2='12'/>
            <line x1='12' y1='16' x2='12.01' y2='16'/>
          </svg>
          <span><FormattedMessage id='coverage.warning'/></span>
        </div>
      )}
    </div>
  );
}
